import { useEffect, useState } from 'react';

import { useGetApiV1PersonSharedStudentEnrollments } from '@/shared/api/generated';
import { parseStudentEnrollment } from '../utils/parseStudentEnrollment';

/** Opção de matrícula de aluno para o Autocomplete de declaração. */
export interface StudentEnrollmentOption {
  /** `courseRegistrationId` (Id da matrícula) — usado para baixar a declaração. */
  readonly courseRegistrationId: string;
  readonly name: string;
  readonly email: string;
  /** Código da turma (ex.: `T-102`); `''` quando ausente. */
  readonly classCode: string;
  /** Rótulo exibido na lista do Autocomplete (nome · e-mail · turma). */
  readonly label: string;
}

/** Monta o rótulo do item juntando os trechos não vazios. */
function buildLabel(name: string, email: string, classCode: string): string {
  return [name, email, classCode].filter(Boolean).join(' · ');
}

/**
 * Busca de matrículas de alunos por nome ou e-mail, com debounce de 1 s e mínimo de 3
 * caracteres — espelha o padrão de {@link useClassSearchDropdown}.
 *
 * Consome `GET /api/v1/person/shared/student-enrollments` via hook gerado pelo Kubb e
 * mapeia cada `DropDownListStringKeyViewModel` (`key` = `courseRegistrationId`,
 * `value` = `"Nome [Email] Turma"`) para {@link StudentEnrollmentOption}.
 *
 * @param filter - Texto digitado no campo de busca (default `''`).
 * @returns `{ students, isLoading, isQuerySuccess }`.
 */
export function useStudentEnrollmentSearch(filter = '') {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1PersonSharedStudentEnrollments(
    isReady ? { filter: debouncedFilter } : undefined,
    { query: { enabled: isReady } },
  );

  const students: readonly StudentEnrollmentOption[] = (data ?? []).map(item => {
    const { name, email, classCode } = parseStudentEnrollment(item.value);
    return {
      courseRegistrationId: String(item.key ?? ''),
      name,
      email,
      classCode,
      label: buildLabel(name, email, classCode),
    };
  });

  return { students, isLoading, isQuerySuccess: isSuccess } as const;
}
